'use client';

import { c as _c } from "react/compiler-runtime";
import { Skeleton } from '@common/components/atom/Skeleton/Skeleton';
import { cn } from '@core/utils';
import type { GiftOrderProductReviewDto } from '@generated/auth';
import { Suspense } from 'react';
import type { GiftProductDetail } from '@/api/getGiftProductDetail';
import { EventRewardCard } from '@/app/donation/_components/EventRewardCard/EventRewardCard';
import { DONATION_DESKTOP_STICKY_TOP } from '@/app/donation/constants';
import type { GiftSaleStatus } from '@/app/donation/types';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { TABS } from '../../constants';
import { GiftDetailTab } from '../GiftDetailTab';
import { GiftTabContentBasic } from '../GiftTabContent/_components/GiftTabContentBasic';
import { GiftTabContentDelivery } from '../GiftTabContent/_components/GiftTabContentDelivery';
import { GiftTabContentInfo } from '../GiftTabContent/_components/GiftTabContentInfo';
import { GiftTabContentInquiry } from '../GiftTabContent/_components/GiftTabContentInquiry';
import { GiftTabContentReview } from '../GiftTabContent/_components/GiftTabContentReview';
import { HeaderInfoSection } from './HeaderInfoSection';
import { PrefillForm } from './PrefillForm';
import { ResponsiveDivider } from './ResponsiveDivider';
import { ReviewShortcutSection } from './ReviewShortcutSection';
interface GiftDetailTemplateProps {
  title: string;
  heroImageUrlList: string[];
  adultYN: boolean;
  institutionName: string;
  productSaleStatus: GiftSaleStatus;
  productName: string;
  reviewRating?: number;
  reviewCount: number;
  productNumber: string;
  requiredDonationAmount: number;
  onlineProductYN: boolean;
  onlineProductDesc?: string;
  exchangeYN: boolean;
  reviewList: GiftOrderProductReviewDto[];
  reviewTotalCount: number;
  photoReviewList: {
    fileUrl: string;
    reviewId: number;
  }[];
  photoReviewCount: number;
  detailContentInfoList: GiftProductDetail['detailContentInfoList'];
  productInfo: string;
  deliveryCompanyName?: string;
  providerName?: string;
  providerBusinessNumber?: string;
  providerContactNumber?: string;
  providerAddress?: string;
  productData?: GiftProductDetail;
  noticeTitle?: string;
  noticeContents?: string;
}
const GIFT_DETAIL_SIDEBAR_STICKY_TOP = 218;
export const GiftDetailTemplate = t0 => {
  const $ = _c(76);
  const {
    title,
    heroImageUrlList,
    adultYN,
    institutionName,
    productSaleStatus,
    productName,
    reviewCount,
    reviewRating,
    productNumber,
    requiredDonationAmount,
    exchangeYN,
    onlineProductYN,
    onlineProductDesc,
    reviewList,
    reviewTotalCount,
    photoReviewList,
    photoReviewCount,
    detailContentInfoList,
    productInfo,
    deliveryCompanyName,
    providerName,
    providerBusinessNumber,
    providerContactNumber,
    providerAddress,
    productData,
    noticeTitle,
    noticeContents
  } = t0;
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  let t1;
  if ($[0] !== reviewCount) {
    t1 = [...(reviewCount < 1 ? [TABS.REVIEW] : [])];
    $[0] = reviewCount;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const disabledTabs = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("mt-20 content-container", "tablet:mt-0 tablet:px-0", "mobile:mt-0 mobile:px-0");
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("mb-100 px-40", "mobile-tablet:mb-20 mobile-tablet:px-0");
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== adultYN || $[5] !== exchangeYN || $[6] !== heroImageUrlList || $[7] !== institutionName || $[8] !== onlineProductDesc || $[9] !== onlineProductYN || $[10] !== productData || $[11] !== productName || $[12] !== productNumber || $[13] !== productSaleStatus || $[14] !== requiredDonationAmount || $[15] !== reviewCount || $[16] !== reviewRating || $[17] !== title) {
    t4 = <HeaderInfoSection title={title} heroImageUrlList={heroImageUrlList} adultYN={adultYN} institutionName={institutionName} productSaleStatus={productSaleStatus} productName={productName} reviewCount={reviewCount} reviewRating={reviewRating} productNumber={productNumber} requiredDonationAmount={requiredDonationAmount} exchangeYN={exchangeYN} onlineProductYN={onlineProductYN} onlineProductDesc={onlineProductDesc} productData={productData} className={t3} />;
    $[4] = adultYN;
    $[5] = exchangeYN;
    $[6] = heroImageUrlList;
    $[7] = institutionName;
    $[8] = onlineProductDesc;
    $[9] = onlineProductYN;
    $[10] = productData;
    $[11] = productName;
    $[12] = productNumber;
    $[13] = productSaleStatus;
    $[14] = requiredDonationAmount;
    $[15] = reviewCount;
    $[16] = reviewRating;
    $[17] = title;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cn("mb-100 px-40", "mobile-tablet:hidden");
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== reviewCount || $[21] !== reviewList) {
    t6 = <ReviewShortcutSection reviewCount={reviewCount} items={reviewList} className={t5} />;
    $[20] = reviewCount;
    $[21] = reviewList;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = cn("sticky z-10 mx-40 bg-Gray-white", "mobile-tablet:top-[48px] mobile-tablet:mx-0");
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== isDesktopViewport) {
    t8 = isDesktopViewport ? {
      top: DONATION_DESKTOP_STICKY_TOP
    } : undefined;
    $[24] = isDesktopViewport;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== disabledTabs || $[27] !== productNumber || $[28] !== t8) {
    t9 = <GiftDetailTab productNumber={productNumber} disabledTabs={disabledTabs} className={t7} style={t8} />;
    $[26] = disabledTabs;
    $[27] = productNumber;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = cn("mt-40 grid grid-cols-[1fr_370px] gap-80 px-40", "mobile-tablet:mt-0 mobile-tablet:grid-cols-1 mobile-tablet:px-0");
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== detailContentInfoList || $[32] !== noticeContents || $[33] !== noticeTitle || $[34] !== productName) {
    t11 = <GiftTabContentBasic detailContentInfoList={detailContentInfoList} productName={productName} noticeTitle={noticeTitle} noticeContents={noticeContents} />;
    $[31] = detailContentInfoList;
    $[32] = noticeContents;
    $[33] = noticeTitle;
    $[34] = productName;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ResponsiveDivider />;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== productInfo) {
    t13 = <GiftTabContentInfo productInfo={productInfo} />;
    $[37] = productInfo;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ResponsiveDivider />;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== deliveryCompanyName || $[41] !== providerAddress || $[42] !== providerBusinessNumber || $[43] !== providerContactNumber || $[44] !== providerName) {
    t15 = <GiftTabContentDelivery deliveryCompanyName={deliveryCompanyName} providerName={providerName} providerBusinessNumber={providerBusinessNumber} providerContactNumber={providerContactNumber} providerAddress={providerAddress} />;
    $[40] = deliveryCompanyName;
    $[41] = providerAddress;
    $[42] = providerBusinessNumber;
    $[43] = providerContactNumber;
    $[44] = providerName;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <ResponsiveDivider />;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== photoReviewCount || $[48] !== photoReviewList || $[49] !== productNumber || $[50] !== reviewCount || $[51] !== reviewList || $[52] !== reviewRating || $[53] !== reviewTotalCount) {
    t17 = <GiftTabContentReview reviewRating={reviewRating} reviewCount={reviewCount} reviewList={reviewList} reviewTotalCount={reviewTotalCount} photoReviewList={photoReviewList} photoReviewCount={photoReviewCount} productNumber={productNumber} />;
    $[47] = photoReviewCount;
    $[48] = photoReviewList;
    $[49] = productNumber;
    $[50] = reviewCount;
    $[51] = reviewList;
    $[52] = reviewRating;
    $[53] = reviewTotalCount;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  let t18;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <ResponsiveDivider />;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  let t19;
  if ($[56] !== productNumber) {
    t19 = <GiftTabContentInquiry productNumber={productNumber} />;
    $[56] = productNumber;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  let t20;
  if ($[58] !== t11 || $[59] !== t13 || $[60] !== t15 || $[61] !== t17 || $[62] !== t19) {
    t20 = <div className="flex min-w-0 flex-col">{t11}{t12}{t13}{t14}{t15}{t16}{t17}{t18}{t19}</div>;
    $[58] = t11;
    $[59] = t13;
    $[60] = t15;
    $[61] = t17;
    $[62] = t19;
    $[63] = t20;
  } else {
    t20 = $[63];
  }
  let t21;
  if ($[64] !== isDesktopViewport || $[65] !== productData || $[66] !== productNumber) {
    t21 = isDesktopViewport && <div><div className="sticky mb-44 rounded-[36px] border-1 px-28 py-44" style={{
        top: GIFT_DETAIL_SIDEBAR_STICKY_TOP
      }}><PrefillForm productData={productData} productNumber={productNumber} className="gap-80" /><Suspense fallback={<Skeleton className="mt-24 h-56 w-full rounded-medium" />}><EventRewardCard productNumber={productNumber} defaultExpanded={true} className="mt-24" /></Suspense></div></div>;
    $[64] = isDesktopViewport;
    $[65] = productData;
    $[66] = productNumber;
    $[67] = t21;
  } else {
    t21 = $[67];
  }
  let t22;
  if ($[68] !== t20 || $[69] !== t21) {
    t22 = <div className={t10}>{t20}{t21}</div>;
    $[68] = t20;
    $[69] = t21;
    $[70] = t22;
  } else {
    t22 = $[70];
  }
  let t23;
  if ($[71] !== t22 || $[72] !== t4 || $[73] !== t6 || $[74] !== t9) {
    t23 = <div className={t2}>{t4}{t6}{t9}{t22}</div>;
    $[71] = t22;
    $[72] = t4;
    $[73] = t6;
    $[74] = t9;
    $[75] = t23;
  } else {
    t23 = $[75];
  }
  return t23;
};